import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { L10nTranslationService } from 'angular-l10n';
import { BehaviorSubject, Observable, Subscription, filter, of, tap } from 'rxjs';
import { EventsService } from 'src/app/boxwood-api';
import { DirectoriesGetItemsRequestParams, DirectoriesService } from 'src/app/directory-api';
import { AlertDefaults, AlertSettings, FeatureWarningsResponse, AlertType, BasicMessage, Brand, ChannelSettings, DistributionProfileSummary, Facility, FacilitySettings, Field, FileDetails, IncidentEvent, IncidentSettings, IncidentsApi, JobsApi, LocationSettings, MapApi, MessageSettings, Person, PollQuestionModel, PreviewAlertRequest, PriorityType, Questionnaire, QuestionnairesApi, RecipientSettings, RelatedAlertActions, ScheduleSettings, SendAlertResponse, SenderSettings, SettingsApi, SeverityAssessment, SocialApi, SocialMediaAccountModel, SurveySettings, Template, TemplatesApi, User } from 'src/app/hickory-api';
import { ApiConfig } from 'src/app/shared/api-config/api-config';
import { AccessControl } from 'src/app/shared/authentication/access-control';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { Directory, DirectoryGroup, DirectoryUser, PseudoDirectory } from 'src/app/shared/directory/base/models';
import { FeatureToggleService } from 'src/app/shared/feature-toggle.service';
import { HtmlHelper } from 'src/app/shared/html-helper/html-helper';
import { LogHelper } from 'src/app/shared/logging/log-helper';
import { RetryInfo, setImpersonationToken } from 'src/app/shared/model';
import { ApiError, deepCopy, findDiffs, isInteger } from 'src/app/shared/model/utility';
import { SnackbarService } from 'src/app/shared/snackbar.service';
import { MapLocation, MapSelection } from '../../models';
import { AlertLocationService } from '../alert-location/alert-location.service';
import { AlertPreviewJobHelper } from '../alert-preview/alert-preview.job.helper';
import { AlertConfig } from './alert-config';
import { AlertApiHelper, ExtSendAlertRequest } from './helpers/alert-api-helper';
import { AlertBrandsHelper } from './helpers/alert-brands-helper';
import { AlertChannelsHelper, BasicChannel } from './helpers/alert-channels-helper';
import { AlertDefaultsHelper } from './helpers/alert-defaults-helper';
import { AlertFacilitiesHelper } from './helpers/alert-facilities-helper';
import { AlertFields, AlertFieldsHelper } from './helpers/alert-fields-helper';
import { AlertGeofencesHelper } from './helpers/alert-geofences-helper';
import { AlertIncidentHelper } from './helpers/alert-incident-helper';
import { AlertLocationHelper } from './helpers/alert-location-helper';
import { AlertMessageHelper } from './helpers/alert-message-helper';
import { AlertPollHelper } from './helpers/alert-poll-helper';
import { AlertPriorityHelper } from './helpers/alert-priority-helper';
import { AlertRecipientsHelper } from './helpers/alert-recipients-helper';
import { AlertScheduleHelper } from './helpers/alert-schedule-helper';
import { AlertSenderHelper } from './helpers/alert-sender-helper';
import { AlertSocialHelper } from './helpers/alert-social-helper';
import { AlertSurveyHelper } from './helpers/alert-survey-helper';
import { AlertTemplateHelper } from './helpers/alert-template-helper';
import { AlertTextMessageHelper, TextMessagePreviewData } from './helpers/alert-text-message-helper';
import { AvailableTemplatesService } from 'src/app/shared/templated-input/available-templates.service';
import { AlertWorkflowHelper } from './helpers/alert-workflow-helper';
import { WindowMessage } from 'src/app/shared/messaging';

const tag = 'Alert.Model';

type MapFacility = { mapId: string, facilities: Facility[] };

export interface AlertBuilderState {
  // these are guaranteed to be non-null (could still be empty)
  config: AlertConfig;
  template: Template;
  settings: AlertDefaults;
  files: File[];
  missingUsers: DirectoryUser[];
  missingGroups: DirectoryGroup[];
  bypassChangesCheck: boolean;
  alertPreviewInProgress: boolean;
  alertSendInProgress: boolean;

  // these may be undefined or null
  defaults?: AlertDefaults;
  vanilla?: AlertDefaults;
  brands?: Brand[];
  socialFile?: FileDetails;
  socialAccounts?: SocialMediaAccountModel[];
  textPreviewData?: TextMessagePreviewData;
  questionnaire?: Questionnaire;
  mapLocation?: MapLocation;
  mapSelection?: MapSelection;
  mapFacilities?: MapFacility[];
  adHocList?: DistributionProfileSummary;
  callCenterToken?: string;
  callCenterReporter?: Person;
  preview?: AlertSettings;
  sendResponse?: SendAlertResponse;

  featureWarnings?: FeatureWarningsResponse;
}

const defaultState: AlertBuilderState = {
  config: new AlertConfig(),
  template: AlertTemplateHelper.initTemplate(),
  settings: AlertDefaultsHelper.initDefaults(),
  files: [],
  missingUsers: [],
  missingGroups: [],
  bypassChangesCheck: false,
  alertPreviewInProgress: false,
  alertSendInProgress: false,
  mapFacilities: [],
}

@Injectable()
export class AlertModelService {
  static readonly FULL_TEMPLATE = 'isFullTemplate';
  static readonly OP_PREVIEW = 'fetchPreview';
  static readonly OP_DEFAULTS = 'fetchDefaults';
  static readonly OP_TEMPLATE = 'fetchTemplate';
  static readonly OP_SEND = 'sendAlert';

  /**
   * Creates a "stub" of this service that can process and react to inputs like it normally would.
   * Useful in cases where using static helper methods that require the service as a parameter.
   */
  static stubService(httpClient?: HttpClient, authService?: AuthService): AlertModelService {
    return new AlertModelService(httpClient, authService);
  }

  // cached directories (mapped by tenant id) -- don't reset this when the service is reset
  private readonly directoriesMap = new Map<string, Directory[]>();
  get directories(): Directory[] { return this.directoriesMap.get(ApiConfig.tenantId); }

  // event emitters
  modelEvents: EventEmitter<string> = new EventEmitter();
  errorEvents: EventEmitter<RetryInfo> = new EventEmitter();
  directoryEvents: EventEmitter<string> = new EventEmitter();
  impersonationEvents: EventEmitter<string> = new EventEmitter<string>();

  // helper instances
  alertsApi: AlertApiHelper;
  previewJobPoller: AlertPreviewJobHelper;
  textPreviewHelper: AlertTextMessageHelper;
  windowMessage$ = new BehaviorSubject<WindowMessage>(null);

  // subscription for network operations -- should be cancelled when service is reset
  subscription: Subscription = new Subscription();

  // this gets reset when the service is reset
  state: AlertBuilderState;

  // derived members
  get config(): AlertConfig { return this.state.config; }
  get alertTemplate(): Template { return this.state.template; }
  get alertSettings(): AlertDefaults { return this.state.settings; }
  get alertDefaults(): AlertDefaults { return this.state.defaults; }
  get vanillaDefaults(): AlertDefaults { return this.state.vanilla; }
  get templateFiles(): FileDetails[] { return this.alertMessage?.files; }
  get impersonationToken() { return this.state.callCenterToken; }
  get callCenterReporter() { return this.state.callCenterReporter; }
  get bypassChangesCheck() { return this.state.bypassChangesCheck; }
  get alertPreviewInProgress() { return this.state.alertPreviewInProgress; }
  get alertSendInProgress() { return this.state.alertSendInProgress; }
  get alertPreview() { return this.state.preview; }
  get alertResponse() { return this.state.sendResponse; }
  get mapLocation() { return this.state.mapLocation; }
  get mapSelection() { return this.state.mapSelection; }
  get mapFacilities() { return this.state.mapFacilities; }
  get missingUsers() { return this.state.missingUsers; }
  get missingGroups() { return this.state.missingGroups; }
  get tenantBrands() { return this.state.brands; }
  get selectedFiles() { return this.state.files; }
  get socialFile() { return this.state.socialFile; }
  get socialAccounts() { return this.state.socialAccounts; }
  get adHocList() { return this.state.adHocList; }
  get questionnaire() { return this.state.questionnaire; }

  get alertType(): AlertType { return this.alertSettings.alertType || AlertType.Default; }
  get alertFields(): Field[] { return this.alertSettings.fields || []; }
  get requiredFields(): Field[] { return AlertFieldsHelper.requiredFields(this); }
  get alertChannels(): ChannelSettings { return this.alertSettings.channels; }
  get iterableChannels(): BasicChannel[] { return AlertChannelsHelper.iterableChannels(this); }
  get alertMessage(): MessageSettings { return this.alertSettings.message; }
  get alertPoll(): PollQuestionModel { return this.alertMessage.poll; }
  get alertPriority(): PriorityType { return this.useAlertPriorityV2 ? AlertPriorityHelper.getOptionalAlertPriority(this.alertMessage) : AlertPriorityHelper.getAlertPriority(this.alertMessage) ?? PriorityType.Emergency; }
  get basicMessage(): BasicMessage { return this.alertMessage.basic; }
  get rarfMessage(): string { return this.isRarf ? this.alertDefaults?.message?.basic?.messageRichContent : null; }
  get alertIncident(): IncidentSettings { return this.alertMessage.incident; }
  get alertIncidentEvent(): IncidentEvent { return this.alertMessage.incidentEvent; }
  get alertLocation(): LocationSettings { return this.alertSettings.location; }
  get alertFacilities(): Facility[] { return this.alertLocation?.facilities || []; }
  get allFacilityIds(): string[] { return [...(this.alertFacilities?.map(f => f.id) ?? []), ...(this.mapFacilities?.flatMap(f => f.facilities?.map(f => f.id) ?? []) ?? [])]; }
  get alertFacility(): FacilitySettings { return this.alertSettings?.facilities; }
  get alertRecipients(): RecipientSettings { return this.alertSettings.recipients; }
  get alertMandatoryGroupIds(): number[] { return (this.alertRecipients?.mandatoryGroupIds || []); }
  get alertSchedule(): ScheduleSettings { return this.alertSettings.schedule; }
  get alertSender(): SenderSettings { return this.alertSettings.sender; }
  get alertSeverity(): SeverityAssessment { return this.alertMessage.severityAssessment; }
  get alertSurvey(): SurveySettings { return this.alertMessage.survey; }
  get relatedAlertActions(): RelatedAlertActions { return this.alertSettings.relatedAlertActions; }
  get isPanicTemplate(): boolean { return this.alertTemplate.action === Template.ActionEnum.Preview || this.alertTemplate.action === Template.ActionEnum.Send }
  get isSelectedTemplate(): boolean { return isInteger(this.alertTemplate.id); }
  get isAcknowledgmentAlert(): boolean { return this.alertSettings?.requestConfirmation === true; }
  get isPoll(): boolean { return this.alertType === AlertType.Poll; }
  get isRarf(): boolean { return !!this.config.relatedAlertId; }
  get isCallCenter(): boolean { return !!this.config.callCenter; }
  get isResendAlert(): boolean { return !!this.config.resendAlertId; }
  get isEditScheduled(): boolean { return !!this.isResendAlert && this.config.editScheduled; }
  get isEditingTemplate() { return this.config.editTemplate; }
  get currentUser(): User { return this.authService.user.snapshot; }
  get userHasPublicAlert(): boolean { return this.authService.hasPermission(AccessControl.AlertSendPublic); }
  get userHasExternalLink(): boolean { return this.authService.isSatisfied({ all: [AccessControl.AlertChannelPublicLink, AccessControl.AlertSendPublic] }); }
  get userHasVoice(): boolean { return this.authService.hasPermission(AccessControl.AlertChannelVoice); }
  get userHasAcknowledgment(): boolean { return this.authService.hasPermission(AccessControl.AlertAcknowledgment); }
  get usePollWorkflow(): boolean { return this.userHasVoice && this.isPoll && this.authService.hasPermission(AccessControl.AlertVoiceWorkflow); }
  get useVoicePriority(): boolean { return this.userHasVoice && this.authService.hasPermission(AccessControl.AlertVoicePriority); }
  get useMunicipalSelection(): boolean { return this.userHasPublicAlert && this.authService.hasPermission(AccessControl.FeatureRecipientsMunicipalSelection); }
  get showAdHocRecipients(): boolean { return this.authService.hasPermission(AccessControl.FeatureRecipientsAdHoc); }
  get showIndividualRecipient(): boolean { return this.authService.isSatisfied({ all: [AccessControl.FeatureRecipientsIndividual], any: [AccessControl.AlertChannelTextMessage, AccessControl.AlertChannelVoice] }); }
  get useBrandingSendFeature(): boolean { return this.authService.hasPermission(AccessControl.FeatureAlertBranding); }
  get useDirectory(): boolean { return this.authService.hasPermission(AccessControl.FeatureRecipientsDirectories); }
  get useRecipientsV2(): boolean { return this.authService.hasPermission(AccessControl.FeatureRecipientsSelectionNext) || this.useDirectory; }
  get useSplitHickoryDirectory(): boolean { return this.authService.hasPermission(AccessControl.FeatureRecipientsSplitDirectory) && this.authService.hasPermission(AccessControl.FeatureRecipientsSelectionNext); }
  get useSchedulerV2(): boolean { return this.authService.hasPermission(AccessControl.FeatureScheduledAlertsNext); }
  get useAlertPriorityV2(): boolean { return this.authService.hasPermission(AccessControl.FeatureAlertClassification); }
  get useIncidentsV2(): boolean { return this.authService.hasPermission(AccessControl.FeatureIncidentManagementNext); }
  get useAdvancedRecipientSearch(): boolean { return this.authService.hasPermission(AccessControl.FeatureAdvancedRecipientSearch); }
  get useFacilityMapSelection(): boolean { return this.authService.hasPermission(AccessControl.AlertFacilityMapSelection); }

  get featureWarnings(): FeatureWarningsResponse { return this.state.featureWarnings; }

  // custom setters
  set alertTemplate(value: Template) {
    this.state.template = AlertTemplateHelper.initTemplate(value);
    // setting the template resets the working alert copy
    this.state.settings = deepCopy(this.alertTemplate.alertSettings || {});
  }
  set impersonationToken(value: string) {
    this.state.callCenterToken = value;
    setImpersonationToken(this.alertsApi, value);
    setImpersonationToken(this.incidentApiV1, value);
    this.impersonationEvents.emit(value);
  }

  constructor(
    public httpClient?: HttpClient,
    public authService?: AuthService,
    public templatesApi?: TemplatesApi,
    public jobsApi?: JobsApi,
    public locationService?: AlertLocationService,
    public directoryApi?: DirectoriesService,
    public incidentApiV1?: IncidentsApi,
    public incidentApiV2?: EventsService,
    public settingsApi?: SettingsApi,
    public socialApi?: SocialApi,
    public mapApi?: MapApi,
    public questionnaireApi?: QuestionnairesApi,
    public featureToggleService?: FeatureToggleService,
    public snackbar?: SnackbarService,
    public translation?: L10nTranslationService,
    public htmlHelper?: HtmlHelper,
    public placeholderService?: AvailableTemplatesService,
  ) {
    this.alertsApi = new AlertApiHelper(this.httpClient);
    this.state = deepCopy(defaultState);
    this.reset();
  }

  reset() {
    this.resetWithConfig(null, false);
  }

  resetWithRoute(routeSnapshot?: ActivatedRouteSnapshot) {
    this.resetWithConfig(AlertConfig.fromRoute(routeSnapshot), !!routeSnapshot);
  }

  resetForCallCenter(reporter: Person) {
    this.resetWithConfig(AlertConfig.fromCallCenter(reporter), true);
  }

  resetWithConfig(config: AlertConfig, fetchDefaults: boolean) {
    if (!config) config = new AlertConfig();
    this.state = deepCopy(defaultState);
    this.state.config = config;

    if (fetchDefaults === true) {
      const saved = this.retrieveTemplateFromStorage();
      if (saved?.alertSettings) {
        config.template = saved;
        config.alertType = saved.alertSettings.alertType ?? saved.alertType ?? config.alertType;
      }
    }

    // cancel operations in progress (if any)
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    this.subscription.add(this.modelEvents.subscribe(event => this.onModelEvent(event)));

    // reset helper instances
    this.previewJobPoller = undefined; // this gets instantiated later
    this.textPreviewHelper = new AlertTextMessageHelper(this, this.placeholderService);

    // reset saved state
    this.alertTemplate = AlertTemplateHelper.initTemplate(deepCopy(config.template), config.alertType);
    this.impersonationToken = undefined;

    // fetch directories if needed
    this.fetchDirectories();

    // reset call center reporter
    this.state.callCenterReporter = config.callCenterReporter;
    this.fetchImpersonationToken();

    // (re-)fetch defaults if requested
    if (fetchDefaults) {
      // if editing a template, also fetch "vanilla" defaults without template selections applied
      if (config.editTemplate) this.fetchDefaults(null, AlertConfig.TRIGGER_VANILLA);
      this.fetchDefaults(null, config.trigger);
    }
  }

  clone(directoryClassification?: string): AlertModelService {
    const clone = new AlertModelService(
      this.httpClient,
      this.authService,
      this.templatesApi,
      this.jobsApi,
      this.locationService,
      this.directoryApi,
      this.incidentApiV1,
      this.incidentApiV2,
      this.settingsApi,
      this.socialApi,
      this.mapApi,
      this.questionnaireApi,
      this.featureToggleService,
      this.snackbar,
      this.translation,
      this.htmlHelper,
      this.placeholderService,
    );

    clone.state = deepCopy(this.state);

    let directories: Directory[] = deepCopy(this.directories);
    // if requested, filter directories by classification
    if (directoryClassification) {
      directories = directories.filter(x => x.classificationType?.toLowerCase() === directoryClassification.toLowerCase());
    }
    clone.directoriesMap.set(ApiConfig.tenantId, directories);

    return clone;
  }

  notifyFieldUpdated(field: string = AlertFields.ALL_FIELDS) {
    this.modelEvents.emit(field || AlertFields.ALL_FIELDS);
  }

  notifyDirectoryUpdated(directoryId: string) {
    this.directoryEvents.emit(directoryId);
  }

  getDirectory(id: string): Directory {
    return this.directories?.find(d => d.id === id);
  }

  private lastDirectoriesRx$: Subscription = null;
  fetchDirectories() {
    if (this.directories?.length) return;

    const emitDirectories = ((dirs: Array<Directory>) => {
      // if we don't have any directories from the API, just show the hickory directories
      if (!dirs.length) dirs = this.useSplitHickoryDirectory ? PseudoDirectory.list() : [PseudoDirectory.single()];
      this.directoriesMap.set(ApiConfig.tenantId, dirs);
      this.directoryEvents.emit('all');
    });

    const dirs = Array<Directory>();
    if (this.useDirectory && !!this.directoryApi) {
      const request: DirectoriesGetItemsRequestParams = { tenantId: ApiConfig.tenantId, page: 1, pageSize: 100, };
      this.lastDirectoriesRx$?.unsubscribe(); // cancel pending, if present
      const rx = this.directoryApi.directoriesGetItems(request).subscribe({
        next: res => {
          res.items?.forEach(d => dirs.push(Directory.fromApi(d)));
          emitDirectories(dirs);
        },
        error: _err => {
          emitDirectories(dirs);
        }
      });
      this.lastDirectoriesRx$ = rx;
      this.subscription.add(rx);
    } else if (this.useRecipientsV2) {
      emitDirectories(dirs);
    }
  }

  fetchImpersonationToken() {
    const reporter = this.callCenterReporter;
    if (reporter) {
      this.authService.createImpersonationToken(reporter).then(token => {
        this.impersonationToken = token;
        this.fetchDefaults();
      });
    }
  }

  fetchDefaults(defaults?: AlertDefaults, trigger?: string, callback?: BehaviorSubject<unknown>) {
    const request = AlertDefaultsHelper.buildRequest(this, defaults, trigger);

    if (trigger === AlertConfig.TRIGGER_VANILLA) {
      this.state.vanilla = undefined;
    } else if (!trigger || trigger === AlertFields.TEMPLATE) {
      this.state.defaults = undefined;
    }

    callback?.next(null);
    this.subscription.add(this.alertsApi.getAlertsDefault(request).subscribe({
      next: res => {
        callback?.next(res?.item);
        this.applyDefaults(res?.item, trigger);
      },
      error: (err: ApiError) => {
        callback?.next(err);
        let errorMessage = 'Failed to retrieve alert options';
        if (err?.statusText) {
          errorMessage = err.statusText
        }
        this.errorEvents.emit(new RetryInfo(
          AlertModelService.OP_DEFAULTS,
          errorMessage,
          err,
          err?.status
        )
        );
      }
    }));
  }

  fetchTemplate(templateId: number, forEdit: boolean = false) {
    if (!templateId || templateId <= 0) return;
    this.subscription.add(this.templatesApi.getTemplatesByIdV3(templateId).subscribe({
      next: res => {
        const template: Template = res?.item;
        this.saveTemplateToStorage(template);
        const alertType = AlertTemplateHelper.getAlertType(template);
        const config = new AlertConfig(template, alertType);
        config.editTemplate = forEdit;
        this.resetWithConfig(config, true);
        this.state.featureWarnings = template.featureWarnings;
      },
      error: (err: ApiError) => {
        this.errorEvents.emit(new RetryInfo(AlertModelService.OP_TEMPLATE, 'Failed to retrieve template options', err));
      }
    }));
  }

  fetchTemplateWarnings(event: string) {
    if (!this.shouldCheckWarnings(event)) return;
    this.setFeatureWarnings(this.templatesApi.getTemplateFeatureWarnings({ alertSettings: this.alertSettings }))
  }

  fetchDefaultsWarnings(event: string) {
    if (!this.shouldCheckWarnings(event)) return;
    this.setFeatureWarnings(this.alertsApi.getAlertDefaultsFeatureWarnings({ alertSettings: this.alertDefaults }))
  }

  private shouldCheckWarnings(event: string): boolean {
    return [
      AlertFields.CHANNELS,
      AlertFields.MESSAGE_PERSONALIZATION,
      AlertFields.RECIPIENTS,
      AlertFields.ALERT_ACK,
      AlertFields.FACILITIES
    ].includes(event);
  }

  private setFeatureWarnings(req$: Observable<FeatureWarningsResponse>) {
    this.subscription.add(req$.subscribe({
      next: res => {
        this.state.featureWarnings = res;
      },
      error: _ => { }
    }));
  }

  applyDefaults(defaults: AlertDefaults, trigger: string) {
    // use hard-coded alert fields for call center alert send
    if (this.isCallCenter) {
      defaults.fields = AlertFieldsHelper.callCenterFields();
    }

    defaults = AlertDefaultsHelper.initDefaults(defaults, defaults.alertType, this.config);
    const didMerge = AlertDefaultsHelper.mergeDefaults(this.alertSettings, defaults, trigger, this);

    // clear MAGS from alert defaults for RARF alerts or if editing a template
    if (this.isEditingTemplate || !!defaults.related?.find(r => r.alertRelationType != null)) {
      defaults.recipients.mandatoryGroupIds = [];
    }

    // if we're editing a template, use the sender values from the template (rather than defaults)
    if (this.isEditingTemplate) {
      defaults.sender = this.config.template?.alertSettings?.sender ?? {};
    }

    if (trigger === AlertConfig.TRIGGER_VANILLA) {
      this.state.vanilla = defaults;
    } else if (didMerge) {
      this.state.defaults = defaults;
      this.notifyFieldUpdated();
    } else {
      // apply the defaults as a new template
      this.state.defaults = defaults;
      const template = AlertTemplateHelper.initTemplate(deepCopy(this.config.template));
      Object.assign(template.alertSettings, deepCopy(defaults));
      this.applyTemplate(template);
    }
  }

  applyTemplate(template: Template) {
    const alert = AlertDefaultsHelper.initDefaults(template.alertSettings);

    // restore template info, if present
    if (!template.id) template.id = this.config.templateId;
    template.name = this.alertTemplate?.name ?? this.config.template?.name ?? template.name;
    template.folderPath = this.alertTemplate?.folderPath ?? this.config.template?.folderPath ?? template?.folderPath;

    if (!this.userHasAcknowledgment || alert.alertType === AlertType.Poll) {
      alert.requestConfirmation = false;
    }

    AlertFieldsHelper.postProcess(this, alert);
    AlertMessageHelper.postProcess(this, alert);
    AlertPollHelper.postProcess(this, alert);
    AlertChannelsHelper.postProcess(this, alert);
    AlertSocialHelper.postProcess(this, alert);
    AlertSenderHelper.postProcess(this, alert);
    AlertScheduleHelper.postProcess(this, alert);
    AlertRecipientsHelper.postProcess(this, alert);
    AlertFacilitiesHelper.postProcess(this, alert);
    AlertBrandsHelper.postProcess(this, alert);
    AlertIncidentHelper.postProcess(this, alert);
    AlertLocationHelper.postProcess(this, alert);
    AlertGeofencesHelper.postProcess(this, alert);

    this.alertTemplate = template;
    this.notifyFieldUpdated();
  }

  buildPreviewRequest(forTemplate: boolean = false, jobId?: string): PreviewAlertRequest {
    const wasEditingTemplate = this.isEditingTemplate;
    if (forTemplate) {
      // the user selected to save an alert as a template from the send alert form, so
      // treat this as if we were editing a template on the template form
      this.state.config.editTemplate = true;
    }

    const alert = AlertDefaultsHelper.initDefaults();
    Object.assign(alert, deepCopy(this.alertSettings)); alert.id = undefined;
    AlertChannelsHelper.preparePreview(this, alert);
    AlertSocialHelper.preparePreview(this, alert);
    AlertMessageHelper.preparePreview(this, alert);
    AlertIncidentHelper.preparePreview(this, alert);
    AlertSenderHelper.preparePreview(this, alert);
    AlertScheduleHelper.preparePreview(this, alert);
    AlertPollHelper.preparePreview(this, alert);
    AlertRecipientsHelper.preparePreview(this, alert);
    AlertLocationHelper.preparePreview(this, alert);
    AlertGeofencesHelper.preparePreview(this, alert);
    AlertBrandsHelper.preparePreview(this, alert);
    AlertSurveyHelper.preparePreview(this, alert);
    AlertWorkflowHelper.preparePreview(this, alert);
    AlertFacilitiesHelper.preparePreview(this, alert);

    // set this value back to what it was before
    this.state.config.editTemplate = wasEditingTemplate;

    // these attachments have already been uploaded, so they can be included in the preview
    alert.message.files = deepCopy(this.templateFiles);

    return { settings: alert, jobId };
  }

  private setupPreviewJobPoller() {
    this.previewJobPoller?.stopPolling();
    this.previewJobPoller = new AlertPreviewJobHelper(this.authService.user?.snapshot?.tenantId, this.jobsApi);
  }

  fetchPreview() {
    const op = AlertModelService.OP_PREVIEW;
    this.state.preview = undefined;
    this.state.alertPreviewInProgress = true;
    this.setupPreviewJobPoller();
    this.previewJobPoller
      .createJob()
      .subscribe((job) => {
        this.notifyFieldUpdated(op);
        this.subscription.add(this.previewJobPoller.getPoller().subscribe());
        let success = false;
        const finish$ = of(null)
          .pipe(
            tap(() => this.state.alertPreviewInProgress = false),
            filter(() => success),
            tap(() => this.notifyFieldUpdated(op))
          );

        this.alertsApi.createAlertsPreview(this.buildPreviewRequest(false, job?.id)).subscribe({
          next: (res) => {
            if (res?.item && !res.hasErrors) {
              success = true;
              AlertScheduleHelper.fixDates(this, res.item?.schedule);
              this.state.preview = res.item;
            } else {
              const message = res.status?.errorMessage || 'Something isn\'t right, please check your settings.';
              this.errorEvents.emit(new RetryInfo(op, message, null));
            }
            this.previewJobPoller ? this.previewJobPoller.stopPolling(finish$) : finish$.subscribe();
          },
          error: (err) => {
            const message = 'Failed to retrieve alert preview';
            this.errorEvents.emit(new RetryInfo(op, message, err));
            this.previewJobPoller ? this.previewJobPoller.stopPolling(finish$) : finish$.subscribe();
          }
        });
      });
  }

  buildSendRequest(): ExtSendAlertRequest {
    const alert: AlertDefaults = deepCopy(this.state.preview);
    alert.fields = this.alertFields; // ROAD-6600: add the alert fields to the request
    const request: ExtSendAlertRequest = {
      settings: alert,
      files: this.state.files,
      // the social media file will be captured from settings.message.fileDetails
      //socialMediaFile: this.state.socialFile,
      // don't include the templateId on the request, even if sending from a template
      // see: ROAD-381, ROAD-6054, ROAD-8176, ROAD-8487
    }

    return request;
  }

  sendAlert() {
    const op = AlertModelService.OP_SEND;
    this.state.sendResponse = undefined;
    this.state.alertSendInProgress = true;
    this.notifyFieldUpdated(op);
    this.alertsApi.sendAlert(this.buildSendRequest()).subscribe({
      next: res => {
        this.state.alertSendInProgress = false;
        this.state.sendResponse = res;
        if (this.isEditScheduled && !this.alertSchedule?.scheduledAlertSettingsId) {
          // for legacy scheduled alerts, we need to unschedule the original
          const oldAlertId = this.config.resendAlertId;
          this.unscheduleAlert(oldAlertId, op);
        } else {
          this.notifyFieldUpdated(op);
        }
      }, error: err => {
        this.state.alertSendInProgress = false;
        const message = 'Failed to send alert';
        this.errorEvents.emit(new RetryInfo(op, message, err));
      }
    });
  }

  unscheduleAlert(alertId: number, notifyEvent: string) {
    if (!alertId) { this.notifyFieldUpdated(notifyEvent); return; }
    this.alertsApi.unscheduleAlert(alertId).subscribe({
      next: _ => {
        this.notifyFieldUpdated(notifyEvent);
      }, error: _err => {
        this.notifyFieldUpdated(notifyEvent);
      }
    });
  }

  /**
   * @returns true if the working copy of AlertDefaults differs from the template copy
   * Note: if alertResponse is set or if bypassChangesCheck == true, this returns false
   */
  hasUserChanges(): boolean {
    if (!!this.state.sendResponse || this.bypassChangesCheck) {
      this.state.bypassChangesCheck = false;
      return false;
    }

    // compare original (from defaults) copy against current working copy
    const alertOriginal = this.alertTemplate.alertSettings;
    const alertCurrent = this.alertSettings;

    // we ignore differences for these keys b/c we either added them programmatically
    // or (in the case of `messageBody`), we're handling them separately.
    const ignoreKeys = ['messageBody', 'channelKey', 'channelName', 'externalLink', 'publicContentId', 'socialType'];
    const diffs = findDiffs(alertOriginal, alertCurrent, { ignoreKeys: ignoreKeys, failFast: true });

    if (!diffs.length) {
      // now check the message HTML for changes, normalizing first
      const htmlOriginal = HtmlHelper.normalizeHtml(alertOriginal?.message?.basic?.messageBody);
      const htmlCurrent = HtmlHelper.normalizeHtml(alertCurrent?.message?.basic?.messageBody);
      if (htmlOriginal !== htmlCurrent) diffs.push('message.basic.messageBody');
    }

    if (diffs.length) LogHelper.debug(tag, 'hasUserChanges() found changes in:', diffs[0]);
    return !!diffs.length;
  }

  saveTemplateToStorage(template: Template) {
    sessionStorage.setItem('template', JSON.stringify(template));
  }

  retrieveTemplateFromStorage(): Template {
    const template = JSON.parse(sessionStorage.getItem('template'));
    sessionStorage.removeItem('template');
    return template;
  }

  readyForPreview(): boolean {
    return this.isEditingTemplate || (!this.requiredFieldsIncomplete()?.length && !this.hasInvalidConfiguration());
  }

  findField(field: string, fields: Field[] = this.alertFields): Field {
    if (!field || !fields) return null;
    return fields.find(f => f.name === field);
  }

  fieldIsRequired(fieldName: string): boolean {
    return !!this.findField(fieldName)?.required;
  }

  fieldIsReadonly(fieldName: string): boolean {
    return !!this.findField(fieldName)?.readonly;
  }

  fieldIsComplete(fieldName: string): boolean {
    return AlertFieldsHelper.isComplete(this, fieldName);
  }

  fieldSummaryText(fieldName: string): string {
    return AlertFieldsHelper.summaryText(this, fieldName);
  }

  hasInvalidConfiguration(): boolean {
    if (!this.findField(AlertFields.FACILITIES) || !this.findField(AlertFields.INCIDENT_V2)) {
      return false;
    }

    return !AlertFacilitiesHelper.isValidForIncidents(this);
  }

  requiredFieldsIncomplete(): string[] {
    const incomplete = [];
    const required = this.requiredFields.map(f => f.name);

    // some fields are required by default
    if (!required.includes(AlertFields.SUBJECT)) required.push(AlertFields.SUBJECT);
    if (!required.includes(AlertFields.CHANNELS)) required.push(AlertFields.CHANNELS);
    if (!required.includes(AlertFields.SCHEDULE)) required.push(AlertFields.SCHEDULE);
    if (this.isPoll) {
      if (!required.includes(AlertFields.POLL_QUESTION)) required.push(AlertFields.POLL_QUESTION);
      if (!required.includes(AlertFields.POLL_OPTIONS)) required.push(AlertFields.POLL_OPTIONS);
    } else {
      if (!required.includes(AlertFields.MESSAGE)) required.push(AlertFields.MESSAGE);
    }

    required.forEach(name => {
      if (!this.fieldIsComplete(name)) incomplete.push(name);
    });

    return incomplete;
  }

  getAdvancedSettingsFields() {
    return this.alertFields.filter(f => AlertFieldsHelper.isAdvancedSettingsField(f));
  }

  onModelEvent(event: string) {
    LogHelper.debug(tag, 'on change:', event);
    if ([AlertFields.ALL_FIELDS, AlertFields.CHANNELS, AlertFields.GEO_FENCING, AlertFields.RECIPIENTS].includes(event)) {
      const updated = AlertChannelsHelper.autoCheckPriorityAgainstEtn(this);
      if (updated) {
        // no need to notify via notifyFieldUpdated() here -- that happens automatically in this case
        this.snackbar.warn('AlertClassification.PriorityChangedToEmergencyForEtn');
      }
    }
    if ([AlertFields.PRIORITY].includes(event)) {
      const updated = AlertChannelsHelper.autoDeselectEtnIfNonEmergency(this);
      if (updated) {
        // no need to notify via notifyFieldUpdated() here -- that happens automatically in this case
        this.snackbar.warn('AlertClassification.EtnDeselectedForNonEmergency');
      }
    }
  }
}
